// AAC-LC bitstream decoder: raw_data_block → elements → quantized spectra →
// dequantized MDCT coefficients (ISO/IEC 14496-3, 4.4 and 4.6).
//
// It stops at the frequency domain. TNS, the inverse MDCT and windowing live
// in the test-only module aacsynth.ts, which checks this parser against ffmpeg.
import { SF_BITS, SF_CODES, SPECTRAL, SWB_LONG, SWB_SHORT } from './aactables';

export const ONLY_LONG = 0, LONG_START = 1, EIGHT_SHORT = 2, LONG_STOP = 3;
export const ZERO_HCB = 0, ESC_HCB = 11, NOISE_HCB = 13, INTENSITY_HCB2 = 14, INTENSITY_HCB = 15;
export const SCE = 0, CPE = 1, CCE = 2, LFE = 3, DSE = 4, PCE = 5, FIL = 6, END = 7;
export const ELEMENT_NAMES = ['SCE', 'CPE', 'CCE', 'LFE', 'DSE', 'PCE', 'FIL', 'END'];
export const SAMPLE_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];

/** The sampling frequency index for a rate. Rates off the table use the nearest table (4.6.1, Table 4.82). */
export function rateIndex(rate: number): number {
  const exact = SAMPLE_RATES.indexOf(rate);
  if (exact >= 0) return exact;
  const lim = [92017, 75132, 55426, 46009, 37566, 27713, 23004, 18783, 13856, 11502, 9391];
  for (let i = 0; i < lim.length; i++) if (rate >= lim[i]) return i;
  return 11;
}

// ---------------------------------------------------------------------------
// Bit reader. Reads MSB-first; n ≤ 25 bits per read().

export class Bits {
  pos: number;
  constructor(readonly d: Uint8Array, startByte: number, readonly endBit: number) {
    this.pos = startByte * 8;
  }
  read(n: number): number {
    if (n === 0) return 0;
    const p = this.pos, d = this.d, i = p >> 3;
    const w = ((d[i] << 24) | (d[i + 1] << 16) | (d[i + 2] << 8) | d[i + 3]) >>> 0;
    this.pos = p + n;
    return ((w << (p & 7)) >>> 0) >>> (32 - n);
  }
  bit(): number {
    const p = this.pos++;
    return (this.d[p >> 3] >> (7 - (p & 7))) & 1;
  }
  check(): void {
    if (this.pos > this.endBit) throw new Error('The data runs past the end of the frame.');
  }
}

// ---------------------------------------------------------------------------
// Huffman codebooks

/** Binary tree: node n has children at [2n] and [2n+1]; a negative entry is a leaf (~symbol). */
function buildTree(codes: ArrayLike<number>, lens: ArrayLike<number>): Int32Array {
  const tree = new Int32Array(codes.length * 2 + 2);
  let next = 1;
  for (let s = 0; s < codes.length; s++) {
    let n = 0;
    for (let b = lens[s] - 1; b >= 0; b--) {
      const slot = n * 2 + ((codes[s] >>> b) & 1);
      if (b === 0) tree[slot] = ~s;
      else {
        if (tree[slot] === 0) tree[slot] = next++;
        n = tree[slot];
      }
    }
  }
  return tree;
}

function huff(r: Bits, t: Int32Array): number {
  let n = 0;
  for (let guard = 0; guard < 24; guard++) {
    const v = t[n * 2 + r.bit()];
    if (v < 0) return ~v;
    if (v === 0) break;
    n = v;
  }
  throw new Error('Invalid Huffman code.');
}

interface Book {
  tree: Int32Array;
  dim: number;
  signed: boolean;
  /** Tuple values, 4 per codeword index (unused slots are 0). Unsigned books store magnitudes. */
  vals: Int8Array;
}

let sfTree: Int32Array | undefined;
let books: Book[] | undefined;

function tables(): { sf: Int32Array; books: Book[] } {
  if (!sfTree || !books) {
    sfTree = buildTree(SF_CODES, SF_BITS);
    books = SPECTRAL.map(([codes, lens], i) => {
      const cb = i + 1;
      const dim = cb <= 4 ? 4 : 2;
      const signed = cb === 1 || cb === 2 || cb === 5 || cb === 6;
      const mod = cb <= 4 ? 3 : cb <= 6 ? 9 : cb <= 8 ? 8 : cb <= 10 ? 13 : 17;
      const off = signed ? (mod - 1) >> 1 : 0;
      const vals = new Int8Array(codes.length * 4);
      for (let idx = 0; idx < codes.length; idx++) {
        let v = idx;
        for (let j = dim - 1; j >= 0; j--) {
          vals[idx * 4 + j] = (v % mod) - off;
          v = Math.floor(v / mod);
        }
      }
      return { tree: buildTree(codes, lens), dim, signed, vals };
    });
  }
  return { sf: sfTree, books };
}

// ---------------------------------------------------------------------------
// Syntax

export interface IcsInfo {
  seq: number;
  shape: number;
  maxSfb: number;
  /** Window group lengths; [1] for long windows. */
  groups: number[];
  numWindows: number;
  swb: readonly number[];
  numSwb: number;
}

export interface TnsFilter {
  length: number;
  order: number;
  direction: number;
  coefRes: number;
  compress: number;
  /** Signed quantized reflection coefficients. */
  coefs: number[];
}

export interface Section {
  g: number;
  start: number;
  end: number;
  cb: number;
}

export interface Ics {
  info: IcsInfo;
  globalGain: number;
  /** Codebook per (group, band), index g * maxSfb + sfb. */
  bandType: Uint8Array;
  /** Scalefactor, noise energy or intensity position per (group, band). */
  sf: Int16Array;
  sections: Section[];
  pulse?: { start: number; offset: number[]; amp: number[] };
  /** TNS filters per window (undefined when TNS is off). */
  tns?: TnsFilter[][];
  /** Quantized values (pulses applied), window w at w * 128. */
  q: Int32Array;
  /** Bit positions: start, after ics_info, after section data, after scalefactors, after pulse/TNS/gain, end. */
  marks: number[];
  /** Bits of section data + scalefactors + spectral data per window group. */
  groupBits: number[];
}

export interface Element {
  id: number;
  tag: number;
  start: number;
  end: number;
  ics: Ics[];
  commonWindow?: boolean;
  msMask?: number;
  msUsed?: Uint8Array;
  /** FIL: first extension payload type and byte count. */
  extType?: number;
  bytes?: number;
}

export interface RawBlock {
  elements: Element[];
  /** Bit position just after the END element. */
  endBit: number;
}

function parseIcsInfo(r: Bits, sfi: number): IcsInfo {
  r.bit(); // ics_reserved_bit
  const seq = r.read(2);
  const shape = r.bit();
  if (seq === EIGHT_SHORT) {
    const maxSfb = r.read(4);
    const grouping = r.read(7);
    const groups: number[] = [];
    let cur = 1;
    for (let i = 6; i >= 0; i--) {
      if ((grouping >> i) & 1) cur++;
      else {
        groups.push(cur);
        cur = 1;
      }
    }
    groups.push(cur);
    const swb = SWB_SHORT[sfi];
    if (maxSfb > swb.length - 1) throw new Error(`max_sfb ${maxSfb} is larger than the ${swb.length - 1} short-window bands.`);
    return { seq, shape, maxSfb, groups, numWindows: 8, swb, numSwb: swb.length - 1 };
  }
  const maxSfb = r.read(6);
  if (r.bit()) throw new Error('predictor_data_present is set. Prediction is only for AAC Main and LTP, not AAC-LC.');
  const swb = SWB_LONG[sfi];
  if (maxSfb > swb.length - 1) throw new Error(`max_sfb ${maxSfb} is larger than the ${swb.length - 1} long-window bands.`);
  return { seq, shape, maxSfb, groups: [1], numWindows: 1, swb, numSwb: swb.length - 1 };
}

function parseTns(r: Bits, info: IcsInfo): TnsFilter[][] {
  const short = info.seq === EIGHT_SHORT;
  const out: TnsFilter[][] = [];
  for (let w = 0; w < info.numWindows; w++) {
    const n = r.read(short ? 1 : 2);
    const filters: TnsFilter[] = [];
    if (n) {
      const coefRes = r.bit();
      for (let f = 0; f < n; f++) {
        const length = r.read(short ? 4 : 6);
        const order = r.read(short ? 3 : 5);
        if (order > (short ? 7 : 12)) throw new Error(`TNS order ${order} is too high for AAC-LC.`);
        const flt: TnsFilter = { length, order, direction: 0, coefRes, compress: 0, coefs: [] };
        if (order) {
          flt.direction = r.bit();
          flt.compress = r.bit();
          const nb = coefRes + 3 - flt.compress;
          for (let i = 0; i < order; i++) {
            const v = r.read(nb);
            flt.coefs.push(v >= 1 << (nb - 1) ? v - (1 << nb) : v);
          }
        }
        filters.push(flt);
      }
    }
    out.push(filters);
  }
  return out;
}

/** individual_channel_stream(). `common` is the shared ics_info of a CPE with a common window. */
function parseIcs(r: Bits, sfi: number, common: IcsInfo | null): Ics {
  const { sf: sfT, books } = tables();
  const m0 = r.pos;
  const globalGain = r.read(8);
  const info = common ?? parseIcsInfo(r, sfi);
  const m1 = r.pos;
  const G = info.groups.length, M = info.maxSfb, short = info.seq === EIGHT_SHORT;
  const bandType = new Uint8Array(G * M);
  const sections: Section[] = [];
  const groupBits = new Array<number>(G).fill(0);
  // section_data: runs of bands that share one codebook.
  const sectBits = short ? 3 : 5, esc = (1 << sectBits) - 1;
  for (let g = 0; g < G; g++) {
    const g0 = r.pos;
    let k = 0;
    while (k < M) {
      const cb = r.read(4);
      if (cb === 12) throw new Error('Codebook 12 is reserved.');
      let len = 0, inc: number;
      do {
        inc = r.read(sectBits);
        len += inc;
      } while (inc === esc && r.pos < r.endBit);
      if (k + len > M) throw new Error('A section runs past max_sfb.');
      r.check();
      if (len === 0) continue;
      bandType.fill(cb, g * M + k, g * M + k + len);
      sections.push({ g, start: k, end: k + len, cb });
      k += len;
    }
    groupBits[g] += r.pos - g0;
  }
  const m2 = r.pos;
  const sf = new Int16Array(G * M);
  const marks = [m0, m1, m2];

  // scale_factor_data: each value is a Huffman-coded difference from the last one of its kind.
  let s = globalGain, isPos = 0, nrg = globalGain - 90, firstNoise = true;
  for (let g = 0; g < G; g++) {
    const g0 = r.pos;
    for (let b = 0; b < M; b++) {
      const idx = g * M + b, cb = bandType[idx];
      if (cb === ZERO_HCB) continue;
      if (cb === INTENSITY_HCB || cb === INTENSITY_HCB2) sf[idx] = isPos += huff(r, sfT) - 60;
      else if (cb === NOISE_HCB) {
        if (firstNoise) {
          firstNoise = false;
          nrg += r.read(9) - 256;
        } else nrg += huff(r, sfT) - 60;
        sf[idx] = nrg;
      } else {
        s += huff(r, sfT) - 60;
        if (s < 0 || s > 255) throw new Error(`Scalefactor ${s} is out of range.`);
        sf[idx] = s;
      }
    }
    groupBits[g] += r.pos - g0;
  }
  marks.push(r.pos);
  r.check();

  let pulse: Ics['pulse'];
  if (r.bit()) {
    if (short) throw new Error('Pulse data is not allowed with short windows.');
    const n = r.read(2) + 1;
    const start = r.read(6);
    if (start >= info.numSwb) throw new Error('Pulse start band is out of range.');
    pulse = { start, offset: [], amp: [] };
    for (let i = 0; i < n; i++) {
      pulse.offset.push(r.read(5));
      pulse.amp.push(r.read(4));
    }
  }
  const tns = r.bit() ? parseTns(r, info) : undefined;
  if (r.bit()) throw new Error('gain_control_data_present is set. Gain control is only for AAC SSR, not AAC-LC.');
  marks.push(r.pos);
  r.check();

  // spectral_data: Huffman-coded tuples of quantized values, band by band.
  const q = new Int32Array(1024);
  const swb = info.swb;
  let win = 0;
  for (let g = 0; g < G; g++) {
    const g0 = r.pos;
    const gl = info.groups[g];
    for (let b = 0; b < M; b++) {
      const cb = bandType[g * M + b];
      if (cb === ZERO_HCB || cb >= NOISE_HCB) continue;
      const bk = books[cb - 1];
      const { tree, dim, signed, vals } = bk;
      for (let w = 0; w < gl; w++) {
        const base = (win + w) * 128;
        const end = base + swb[b + 1];
        for (let k = base + swb[b]; k < end; k += dim) {
          const o = huff(r, tree) * 4;
          if (signed) {
            for (let j = 0; j < dim; j++) q[k + j] = vals[o + j];
          } else {
            for (let j = 0; j < dim; j++) {
              const v = vals[o + j];
              q[k + j] = v && r.bit() ? -v : v;
            }
            if (cb === ESC_HCB) {
              for (let j = 0; j < 2; j++) {
                const v = q[k + j];
                if (v !== 16 && v !== -16) continue;
                let n = 0;
                while (r.bit()) if (++n > 8) throw new Error('Escape sequence is too long.');
                const mag = (1 << (n + 4)) + r.read(n + 4);
                q[k + j] = v < 0 ? -mag : mag;
              }
            }
          }
        }
        if (r.pos > r.endBit) throw new Error('Spectral data runs past the end of the frame.');
      }
    }
    groupBits[g] += r.pos - g0;
    win += gl;
  }
  if (pulse) {
    let k = swb[pulse.start];
    for (let i = 0; i < pulse.offset.length; i++) {
      k += pulse.offset[i];
      if (k > 1023) throw new Error('Pulse position is out of range.');
      q[k] += q[k] > 0 ? pulse.amp[i] : -pulse.amp[i];
    }
  }
  marks.push(r.pos);
  return { info, globalGain, bandType, sf, sections, pulse, tns, q, marks, groupBits };
}

function parseCce(r: Bits, sfi: number, el: Element): void {
  const indSw = r.bit();
  const n = r.read(3);
  let gains = 0;
  for (let c = 0; c <= n; c++) {
    gains++;
    const isCpe = r.bit();
    r.read(4); // cc_target_tag_select
    if (isCpe && r.read(2) === 3) gains++; // cc_l and cc_r both set
  }
  r.bit(); // cc_domain
  r.bit(); // gain_element_sign
  r.read(2); // gain_element_scale
  const ics = parseIcs(r, sfi, null);
  el.ics.push(ics);
  const { sf } = tables();
  for (let c = 1; c < gains; c++) {
    const cge = indSw ? 1 : r.bit();
    if (cge) huff(r, sf);
    else for (let i = 0; i < ics.bandType.length; i++) if (ics.bandType[i] !== ZERO_HCB) huff(r, sf);
  }
}

function parsePce(r: Bits): void {
  r.read(2); // object type
  r.read(4); // sampling frequency index
  const front = r.read(4), side = r.read(4), back = r.read(4), lfe = r.read(2), assoc = r.read(3), cc = r.read(4);
  if (r.bit()) r.read(4);
  if (r.bit()) r.read(4);
  if (r.bit()) r.read(3);
  r.pos += (front + side + back) * 5 + lfe * 4 + assoc * 4 + cc * 5;
  r.pos = (r.pos + 7) & ~7;
  r.pos += r.read(8) * 8;
}

/** Parses one raw_data_block that starts at startByte and must end by endByte. */
export function parseRawBlock(d: Uint8Array, startByte: number, endByte: number, sfi: number): RawBlock {
  const r = new Bits(d, startByte, endByte * 8);
  const elements: Element[] = [];
  for (let guard = 0; guard < 256; guard++) {
    if (r.pos + 3 > r.endBit) throw new Error('The frame ends before the END element.');
    const start = r.pos;
    const id = r.read(3);
    if (id === END) {
      elements.push({ id, tag: 0, start, end: r.pos, ics: [] });
      return { elements, endBit: r.pos };
    }
    const tag = r.read(4);
    const el: Element = { id, tag, start, end: start, ics: [] };
    switch (id) {
      case SCE:
      case LFE:
        el.ics.push(parseIcs(r, sfi, null));
        break;
      case CPE: {
        let common: IcsInfo | null = null;
        el.commonWindow = r.bit() === 1;
        el.msMask = 0;
        if (el.commonWindow) {
          common = parseIcsInfo(r, sfi);
          el.msMask = r.read(2);
          if (el.msMask === 3) throw new Error('ms_mask_present = 3 is reserved.');
          const n = common.groups.length * common.maxSfb;
          el.msUsed = new Uint8Array(n);
          if (el.msMask === 1) for (let i = 0; i < n; i++) el.msUsed[i] = r.bit();
          else if (el.msMask === 2) el.msUsed.fill(1);
        }
        el.ics.push(parseIcs(r, sfi, common));
        el.ics.push(parseIcs(r, sfi, common));
        break;
      }
      case CCE:
        parseCce(r, sfi, el);
        break;
      case DSE: {
        const align = r.bit();
        let count = r.read(8);
        if (count === 255) count += r.read(8);
        if (align) r.pos = (r.pos + 7) & ~7;
        el.bytes = count;
        r.pos += count * 8;
        break;
      }
      case PCE:
        parsePce(r);
        break;
      case FIL: {
        let count = tag;
        if (count === 15) count += r.read(8) - 1;
        el.bytes = count;
        if (count > 0) el.extType = r.read(4);
        r.pos = start + 7 + (tag === 15 ? 8 : 0) + count * 8;
        el.tag = 0;
        break;
      }
    }
    r.check();
    el.end = r.pos;
    elements.push(el);
  }
  throw new Error('Too many elements in one frame.');
}

// ---------------------------------------------------------------------------
// Dequantization and stereo tools

let pow43: Float64Array | undefined;
const POW2SF = new Float64Array(512).map((_, i) => 2 ** ((i - 256) / 4));

export function sfGain(v: number): number {
  return v >= -256 && v < 256 ? POW2SF[v + 256] : 2 ** (v / 4);
}

export interface ChannelSpectrum {
  ics: Ics;
  /** Element and position inside it (0 = left/first, 1 = right). */
  element: Element;
  ch: number;
  /** Dequantized coefficients after M/S and intensity stereo. PNS bands hold a flat level (see `noiseLevel`). */
  coef: Float32Array;
}

/**
 * Turns an ICS into coefficients: sign · |q|^(4/3) · 2^((sf − 100) / 4).
 * Noise (PNS) bands get their per-bin RMS level when `pnsLevel` is true, else 0.
 */
export function dequantize(ics: Ics, pnsLevel = true): Float32Array {
  if (!pow43) pow43 = new Float64Array(8207).map((_, i) => i ** (4 / 3));
  const p43 = pow43;
  const out = new Float32Array(1024);
  const { info, bandType, sf, q } = ics;
  const M = info.maxSfb, swb = info.swb;
  let win = 0;
  for (let g = 0; g < info.groups.length; g++) {
    const gl = info.groups[g];
    for (let b = 0; b < M; b++) {
      const cb = bandType[g * M + b];
      if (cb === ZERO_HCB || cb === INTENSITY_HCB || cb === INTENSITY_HCB2) continue;
      const w0 = swb[b], w1 = swb[b + 1];
      if (cb === NOISE_HCB) {
        if (!pnsLevel) continue;
        const lvl = sfGain(sf[g * M + b]) / Math.sqrt(w1 - w0);
        for (let w = 0; w < gl; w++) out.fill(lvl, (win + w) * 128 + w0, (win + w) * 128 + w1);
        continue;
      }
      const scale = sfGain(sf[g * M + b] - 100);
      for (let w = 0; w < gl; w++) {
        const base = (win + w) * 128;
        for (let k = base + w0; k < base + w1; k++) {
          const v = q[k];
          if (v) out[k] = v > 0 ? p43[v] * scale : -p43[-v] * scale;
        }
      }
    }
    win += gl;
  }
  return out;
}

/** Applies M/S (4.6.8.1) and intensity stereo (4.6.8.2.3) to a CPE's two spectra, in place. */
export function applyStereo(el: Element, l: Float32Array, r: Float32Array): void {
  const [a, b] = el.ics;
  const info = a.info, M = info.maxSfb, swb = info.swb;
  let win = 0;
  for (let g = 0; g < info.groups.length; g++) {
    const gl = info.groups[g];
    for (let s = 0; s < M; s++) {
      const idx = g * M + s;
      if (el.msUsed?.[idx] && a.bandType[idx] < NOISE_HCB && b.bandType[idx] < NOISE_HCB) {
        for (let w = 0; w < gl; w++) {
          const base = (win + w) * 128;
          for (let k = base + swb[s]; k < base + swb[s + 1]; k++) {
            const m = l[k], d = r[k];
            l[k] = m + d;
            r[k] = m - d;
          }
        }
      }
    }
    win += gl;
  }
  // Intensity: the right channel is a scaled copy of the left one.
  const bi = b.info, BM = bi.maxSfb;
  win = 0;
  for (let g = 0; g < bi.groups.length; g++) {
    const gl = bi.groups[g];
    for (let s = 0; s < BM; s++) {
      const idx = g * BM + s, cb = b.bandType[idx];
      if (cb !== INTENSITY_HCB && cb !== INTENSITY_HCB2) continue;
      let c = cb === INTENSITY_HCB ? 1 : -1;
      if (el.msMask) c *= 1 - 2 * (el.msUsed?.[idx] ?? 0);
      const scale = c * sfGain(-b.sf[idx]);
      for (let w = 0; w < gl; w++) {
        const base = (win + w) * 128;
        for (let k = base + bi.swb[s]; k < base + bi.swb[s + 1]; k++) r[k] = l[k] * scale;
      }
    }
    win += gl;
  }
}

/** All output channels (SCE, CPE ×2, LFE) of a block, dequantized, with stereo tools applied. */
export function spectra(block: RawBlock, pnsLevel = true): ChannelSpectrum[] {
  const out: ChannelSpectrum[] = [];
  for (const el of block.elements) {
    if (el.id === SCE || el.id === LFE) out.push({ ics: el.ics[0], element: el, ch: 0, coef: dequantize(el.ics[0], pnsLevel) });
    else if (el.id === CPE) {
      const l = dequantize(el.ics[0], pnsLevel), r = dequantize(el.ics[1], pnsLevel);
      applyStereo(el, l, r);
      out.push({ ics: el.ics[0], element: el, ch: 0, coef: l }, { ics: el.ics[1], element: el, ch: 1, coef: r });
    }
  }
  return out;
}
